import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowRotateRight,
  faArrowUp,
  faGripVertical,
  faImage,
  faLock,
  faUpload,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import {
  inspectionReportService,
  type AvailableReportPhoto,
  type ReportPhotoComposition,
} from '@/services/inspection-report.service';

interface Props {
  inspectionId: string;
  canEdit: boolean;
  /** Suppresses the composer's own heading for callers that title the block. */
  hideHeading?: boolean;
}

/** Mirrors the backend limits (B6.8b). The server stays authoritative — these
 * only stop the UI from offering a composition that would be rejected. */
const MAX_PHOTOS = 8;
const MAX_ROWS = 2;
const MAX_PER_ROW = 4;

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/heic';

/** Maximum caption length — mirrors the column and the server's validator. */
const MAX_CAPTION = 500;

/** One image, the row it sits in, and its caption in THIS report. Array order is
 *  the reading order, and therefore the Evidence Image numbering. */
interface Placement {
  attachmentId: string;
  rowIndex: number;
  /** Report-specific caption. '' means "no caption"; sent as null on save. */
  caption: string;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function isForbidden(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

/**
 * The composition as the engineer should see it.
 *
 * When the rows are authoritative they are used as stored. When they are not —
 * a report composed before B6.8c — the renderer packs the photos two per row,
 * so that is what the composer must show. Reading it this way is what lets the
 * engineer save an unchanged composition and get back exactly the layout they
 * were already looking at.
 */
export function placementsOf(composition: ReportPhotoComposition): Placement[] {
  return composition.photos.map((photo, index) => ({
    attachmentId: photo.attachmentId,
    rowIndex: composition.photosComposed
      ? photo.rowIndex
      : Math.min(Math.floor(index / 2), MAX_ROWS - 1),
    // The report's own caption, never the attachment's global one.
    caption: photo.caption ?? '',
  }));
}

/** Images per row, always MAX_ROWS long. */
function rowCounts(placements: readonly Placement[]): number[] {
  const counts = new Array<number>(MAX_ROWS).fill(0);
  for (const p of placements) if (counts[p.rowIndex] !== undefined) counts[p.rowIndex]! += 1;
  return counts;
}

/** Same images, same order, same rows. */
function samePlacements(a: readonly Placement[], b: readonly Placement[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (p, i) =>
        p.attachmentId === b[i]!.attachmentId &&
        p.rowIndex === b[i]!.rowIndex &&
        // Captions are part of the composition, so editing one makes the
        // composer dirty exactly as reordering does.
        p.caption === b[i]!.caption,
    )
  );
}

/**
 * The composition rules, checked here so the engineer sees the problem before
 * saving. The backend enforces the same rules and remains the authority.
 */
export function validatePlacements(placements: readonly Placement[]): string | null {
  if (placements.length > MAX_PHOTOS) {
    return `A report may include at most ${MAX_PHOTOS} photos.`;
  }
  const counts = rowCounts(placements);
  if (counts.some((c) => c > MAX_PER_ROW)) {
    return `A row may hold at most ${MAX_PER_ROW} images.`;
  }
  if (counts[0] === 0 && counts.some((c, i) => i > 0 && c > 0)) {
    return 'Row 1 must contain an image before Row 2 can be used.';
  }
  return null;
}

/** Reading order must agree with the rows: everything in row 1, then row 2. */
function normalise(placements: readonly Placement[]): Placement[] {
  return [...placements].sort((a, b) => a.rowIndex - b.rowIndex);
}

const ORIGIN_LABEL: Record<AvailableReportPhoto['origin'], string> = {
  INSPECTION_EVIDENCE: 'Inspection photo',
  REPORT_CUSTOM: 'Report image',
};

/** Origin shown as a word, never as colour alone. */
function OriginBadge({ origin }: { origin: AvailableReportPhoto['origin'] }) {
  const custom = origin === 'REPORT_CUSTOM';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
        custom
          ? 'border border-primary/40 bg-primary/10 text-primary'
          : 'border border-border bg-muted text-muted-foreground'
      }`}
      data-testid={`origin-badge-${origin}`}
    >
      <FontAwesomeIcon icon={custom ? faUpload : faImage} className="h-2.5 w-2.5" aria-hidden />
      {ORIGIN_LABEL[origin]}
    </span>
  );
}

function Thumb({
  photo,
  className,
}: {
  photo: AvailableReportPhoto | undefined;
  className: string;
}) {
  const src = photo?.thumbnailUrl ?? photo?.url ?? null;
  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center p-1 text-center`}>
        <span className="text-[10px] text-muted-foreground">
          {photo?.originalFileName ?? 'Image unavailable'}
        </span>
      </div>
    );
  }
  return <img src={src} alt="" className={`${className} object-cover`} />;
}

/** One placed image. Drag listeners live on the handle alone, so dragging can
 * never be mistaken for selecting or removing. */
function PlacedPhoto({
  placement,
  photo,
  position,
  rowLabel,
  canEdit,
  canMove,
  onRemove,
  onMoveRow,
  onCaptionChange,
}: {
  placement: Placement;
  photo: AvailableReportPhoto | undefined;
  /** 1-based place in the GLOBAL order — the Evidence Image number. */
  position: number;
  rowLabel: string;
  canEdit: boolean;
  canMove: boolean;
  onRemove: () => void;
  onMoveRow: () => void;
  onCaptionChange: (caption: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: placement.attachmentId,
    disabled: !canEdit,
  });
  const name = photo?.originalFileName ?? 'photo';
  const toRow = placement.rowIndex === 0 ? 2 : 1;

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="w-40 shrink-0 rounded-lg border border-border bg-card p-1.5"
      data-testid={`selected-photo-${placement.attachmentId}`}
      data-row-index={placement.rowIndex}
      data-position={position}
    >
      <div className="relative h-20 w-full overflow-hidden rounded bg-muted">
        <Thumb photo={photo} className="h-full w-full" />
        <span className="absolute left-1 top-1 rounded bg-foreground/70 px-1 text-[10px] font-medium text-background">
          {position}
        </span>
      </div>

      <div className="mt-1 space-y-1">
        <p className="truncate text-[10px] text-muted-foreground" title={name}>
          {name}
        </p>
        {photo && <OriginBadge origin={photo.origin} />}
        <p className="text-[10px] text-muted-foreground">{rowLabel}</p>

        {/*
          The label the PDF will print. Informational and never an input: the
          number is derived from the image's place in the global order, so it
          renumbers itself the moment the engineer reorders — which is exactly
          the behaviour the report guarantees.
        */}
        <p
          className="text-[10px] font-semibold text-foreground"
          data-testid={`evidence-label-${placement.attachmentId}`}
        >
          Evidence Image {position}
        </p>

        {canEdit ? (
          <label className="block">
            <span className="sr-only">Caption for Evidence Image {position} (optional)</span>
            <textarea
              value={placement.caption}
              onChange={(e) => onCaptionChange(e.target.value)}
              rows={2}
              maxLength={MAX_CAPTION}
              placeholder="Caption (optional)"
              className="w-full resize-none rounded border border-input bg-background px-1.5 py-1 text-[10px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid={`caption-${placement.attachmentId}`}
            />
          </label>
        ) : (
          // Read-only: show the caption if there is one, and nothing at all if
          // there is not — no empty box on a locked report.
          placement.caption.trim() && (
            <p
              className="whitespace-pre-wrap break-words text-[10px] text-foreground"
              data-testid={`caption-text-${placement.attachmentId}`}
            >
              {placement.caption}
            </p>
          )
        )}

        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMoveRow}
              disabled={!canMove}
              className="rounded border border-border px-1 py-0.5 text-[10px] text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={`Move ${name} to row ${toRow}`}
              title={canMove ? `Move to row ${toRow}` : `Row ${toRow} is full`}
              data-testid={`move-photo-${placement.attachmentId}`}
            >
              <FontAwesomeIcon
                icon={placement.rowIndex === 0 ? faArrowDown : faArrowUp}
                className="h-2.5 w-2.5"
                aria-hidden
              />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="rounded border border-border px-1 py-0.5 text-[10px] text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={`Remove ${name} from the report`}
              data-testid={`remove-photo-${placement.attachmentId}`}
            >
              <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" aria-hidden />
            </button>
            <button
              type="button"
              className="ml-auto cursor-grab rounded border border-border px-1 py-0.5 text-[10px] text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={`Reorder ${name} within its row`}
              data-testid={`drag-photo-${placement.attachmentId}`}
              {...attributes}
              {...listeners}
            >
              <FontAwesomeIcon icon={faGripVertical} className="h-2.5 w-2.5" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

export function ReportPhotoSelector({ inspectionId, canEdit, hideHeading }: Props) {
  const qc = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const fileInput = useRef<HTMLInputElement | null>(null);

  const compositionQuery = useQuery({
    queryKey: ['report-photo-composition', inspectionId],
    queryFn: () => inspectionReportService.getPhotoComposition(inspectionId),
    staleTime: 10_000,
  });
  const availableQuery = useQuery({
    queryKey: ['report-photos-available', inspectionId],
    queryFn: () => inspectionReportService.getAvailablePhotos(inspectionId),
    staleTime: 10_000,
  });

  const composition = compositionQuery.data;
  const [draft, setDraft] = useState<Placement[] | null>(null);
  const [saved, setSaved] = useState<Placement[] | null>(null);
  const [saveForbidden, setSaveForbidden] = useState(false);

  /**
   * Seeds the draft from the server exactly once per load. Opening the composer
   * reads and nothing more: no request converts a legacy report to an explicit
   * composition, and re-fetching never discards work in progress.
   */
  useEffect(() => {
    if (!composition) return;
    const next = placementsOf(composition);
    setSaved(next);
    setDraft((current) => current ?? next);
  }, [composition]);

  const photoById = useMemo(() => {
    const map = new Map<string, AvailableReportPhoto>();
    for (const photo of availableQuery.data ?? []) map.set(photo.attachmentId, photo);
    return map;
  }, [availableQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (placements: Placement[]) =>
      // Always the `{ photos }` shape — this is what makes the rows authoritative.
      inspectionReportService.setPhotoComposition(
        inspectionId,
        // A caption is always sent, as text or as an explicit null. That is what
        // lets the server tell "cleared" from "not stated" — an omitted field
        // means "keep the stored caption", which is not what an emptied box means.
        normalise(placements).map((p) => ({
          attachmentId: p.attachmentId,
          rowIndex: p.rowIndex,
          caption: p.caption.trim() || null,
        })),
      ),
    onSuccess: (result: ReportPhotoComposition) => {
      const next = placementsOf(result);
      setDraft(next);
      setSaved(next);
      setSaveForbidden(false);
      qc.setQueryData(['report-photo-composition', inspectionId], result);
      void qc.invalidateQueries({ queryKey: ['report-photos-available', inspectionId] });
      void qc.invalidateQueries({ queryKey: ['report-photos', inspectionId] });
      toast.success('Report photos updated');
    },
    onError: (e: Error) => {
      // The draft is deliberately left intact so the engineer's work survives.
      if (isForbidden(e)) setSaveForbidden(true);
      toast.error(extractMsg(e, 'Failed to update report photos'));
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => inspectionReportService.uploadReportPhoto(inspectionId, file),
    onSuccess: (photo: AvailableReportPhoto) => {
      // Straight into the picker, so the replacement is there to place at once.
      // Deliberately NOT selected: placing an image stays an explicit choice.
      qc.setQueryData<AvailableReportPhoto[]>(
        ['report-photos-available', inspectionId],
        (current) => [...(current ?? []), photo],
      );
      toast.success('Image added to the picker');
    },
    onError: (e: Error) => {
      // The draft is untouched — a failed upload must not cost the engineer
      // the composition they were part-way through building.
      toast.error(extractMsg(e, 'Failed to upload the image'));
    },
  });

  const busy = saveMutation.isPending;

  // ── Read errors ────────────────────────────────────────────────────────────
  // A failure must never be shown as "this report has no photos".
  if (compositionQuery.error) {
    const forbidden = isForbidden(compositionQuery.error);
    return (
      <div className="space-y-2" data-testid="report-photo-selector-error">
        {!hideHeading && (
          <h3 className="text-sm font-semibold text-foreground">Reference Photos</h3>
        )}
        <p className="text-sm text-destructive">
          {forbidden
            ? 'You do not have permission to view the report photos.'
            : extractMsg(compositionQuery.error as Error, 'Could not load the report photos.')}
        </p>
        {!forbidden && (
          <button
            type="button"
            onClick={() => void compositionQuery.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="retry-report-photos"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="h-3 w-3" aria-hidden />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (compositionQuery.isLoading || !composition || draft === null || saved === null) {
    return (
      <div className="space-y-2" data-testid="report-photo-selector-loading">
        {!hideHeading && (
          <h3 className="text-sm font-semibold text-foreground">Reference Photos</h3>
        )}
        <p className="text-sm text-muted-foreground">Loading photos…</p>
      </div>
    );
  }

  const editable = canEdit && !saveForbidden;
  const dirty = !samePlacements(draft, saved);
  /**
   * A legacy report can be saved even with nothing visually changed, because
   * saving IS the change: it converts the packing the renderer improvises into
   * the rows the engineer chose, and photosComposed becomes true. Gating Save on
   * dirtiness alone would make that conversion impossible to perform.
   */
  const convertible = !composition.photosComposed;
  const counts = rowCounts(draft);
  const validationError = validatePlacements(draft);
  const available = availableQuery.data ?? [];
  const selectedIds = new Set(draft.map((p) => p.attachmentId));
  const atMax = draft.length >= MAX_PHOTOS;

  const ordered = normalise(draft);
  const positionOf = new Map(ordered.map((p, i) => [p.attachmentId, i + 1]));

  const rowsToShow = counts[1]! > 0 || editable ? MAX_ROWS : 1;

  /** Adds an image to the first row with space. */
  const select = (attachmentId: string) => {
    setDraft((current) => {
      const list = current ?? [];
      if (list.length >= MAX_PHOTOS || list.some((p) => p.attachmentId === attachmentId)) {
        return list;
      }
      const c = rowCounts(list);
      const rowIndex = c[0]! < MAX_PER_ROW ? 0 : 1;
      if (c[rowIndex]! >= MAX_PER_ROW) return list;
      return normalise([...list, { attachmentId, rowIndex, caption: '' }]);
    });
  };

  /**
   * Edits one image's caption in the draft. Stored against the attachment, so it
   * follows the image through any reorder — the number follows the position,
   * the caption follows the picture.
   */
  const setCaption = (attachmentId: string, caption: string) =>
    setDraft((list) =>
      (list ?? []).map((p) => (p.attachmentId === attachmentId ? { ...p, caption } : p)),
    );

  const deselect = (attachmentId: string) =>
    setDraft((current) => {
      // Removing the last image of row 1 promotes row 2, so no gap is left.
      const kept = (current ?? []).filter((p) => p.attachmentId !== attachmentId);
      const remaining = rowCounts(kept);
      if (remaining[0] === 0 && remaining[1]! > 0) {
        return kept.map((p) => ({ ...p, rowIndex: 0 }));
      }
      return kept;
    });

  const moveRow = (attachmentId: string) =>
    setDraft((current) => {
      const list = current ?? [];
      const entry = list.find((p) => p.attachmentId === attachmentId);
      if (!entry) return list;
      const target = entry.rowIndex === 0 ? 1 : 0;
      const c = rowCounts(list);
      if (c[target]! >= MAX_PER_ROW) return list;
      // Emptying row 1 while row 2 holds images is not a layout the report can
      // have, so the move is refused rather than silently rearranged.
      if (entry.rowIndex === 0 && c[0] === 1 && c[1]! > 0) return list;
      return normalise(
        list.map((p) => (p.attachmentId === attachmentId ? { ...p, rowIndex: target } : p)),
      );
    });

  const clearAll = () => setDraft([]);

  /** Reorders within one row; rows never mix through dragging. */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((current) => {
      const list = normalise(current ?? []);
      const from = list.findIndex((p) => p.attachmentId === String(active.id));
      const to = list.findIndex((p) => p.attachmentId === String(over.id));
      if (from < 0 || to < 0) return list;
      if (list[from]!.rowIndex !== list[to]!.rowIndex) return list;
      return arrayMove(list, from, to);
    });
  };

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-picking the same file fires change again.
    event.target.value = '';
    if (file) uploadMutation.mutate(file);
  };

  return (
    <div className="space-y-4" data-testid="report-photo-selector">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!hideHeading && (
          <h3 className="text-sm font-semibold text-foreground">Reference Photos</h3>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="photo-selection-count">
            Selected: {draft.length} of {available.length}
          </span>
          <span className="text-xs text-muted-foreground" data-testid="photo-maximum">
            Maximum: {MAX_PHOTOS}
          </span>
        </div>
      </div>

      {!editable && (
        <p
          className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground"
          data-testid="photos-frozen-notice"
        >
          <FontAwesomeIcon icon={faLock} className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {saveForbidden
              ? 'You do not have permission to change the report photos.'
              : 'This report is no longer editable. The photos below are the ones it was issued with.'}
          </span>
        </p>
      )}

      {/* ── Composition ──────────────────────────────────────────────────── */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-2" data-testid="photo-rows">
          {Array.from({ length: rowsToShow }, (_, rowIndex) => {
            const rowItems = ordered.filter((p) => p.rowIndex === rowIndex);
            return (
              <div
                key={rowIndex}
                className="rounded-lg border border-border bg-muted/20 p-2"
                data-testid={`photo-row-${rowIndex}`}
              >
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">Row {rowIndex + 1}</span>
                  <span
                    className="text-[10px] text-muted-foreground"
                    data-testid={`photo-row-count-${rowIndex}`}
                  >
                    {rowItems.length} of {MAX_PER_ROW}
                  </span>
                </div>
                {rowItems.length === 0 ? (
                  <p className="px-1 py-2 text-xs text-muted-foreground">
                    {rowIndex === 0
                      ? 'No photos selected for the report yet.'
                      : 'Empty — an empty row is not included in the report.'}
                  </p>
                ) : (
                  <SortableContext
                    items={rowItems.map((p) => p.attachmentId)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <ul className="flex flex-wrap gap-2">
                      {rowItems.map((placement) => (
                        <PlacedPhoto
                          key={placement.attachmentId}
                          placement={placement}
                          photo={photoById.get(placement.attachmentId)}
                          position={positionOf.get(placement.attachmentId) ?? 0}
                          rowLabel={`Row ${placement.rowIndex + 1}`}
                          canEdit={editable && !busy}
                          canMove={
                            counts[placement.rowIndex === 0 ? 1 : 0]! < MAX_PER_ROW &&
                            !(placement.rowIndex === 0 && counts[0] === 1 && counts[1]! > 0)
                          }
                          onRemove={() => deselect(placement.attachmentId)}
                          onMoveRow={() => moveRow(placement.attachmentId)}
                          onCaptionChange={(caption) => setCaption(placement.attachmentId, caption)}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                )}
              </div>
            );
          })}
        </div>
      </DndContext>

      {validationError && (
        <p className="text-xs text-destructive" role="alert" data-testid="photo-validation-error">
          {validationError}
        </p>
      )}

      {/* ── Picker ───────────────────────────────────────────────────────── */}
      {editable && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-xs font-semibold text-foreground">Available Images</h4>
            <div className="flex items-center gap-2">
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                className="sr-only"
                onChange={onFilePicked}
                data-testid="upload-photo-input"
                aria-label="Choose an image to add to this report"
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={uploadMutation.isPending || busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                data-testid="upload-photo-button"
              >
                <FontAwesomeIcon icon={faUpload} className="h-3 w-3" aria-hidden />
                {uploadMutation.isPending ? 'Uploading…' : 'Add custom image'}
              </button>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Uploaded images belong to this report only — they are never added to the inspection
            evidence, and removing one from the report keeps the original file.
          </p>

          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="no-available-photos">
              No images are available yet. Upload one to get started.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="available-photos">
              {available.map((photo) => {
                const isSelected = selectedIds.has(photo.attachmentId);
                const blocked = !isSelected && atMax;
                return (
                  <li key={photo.attachmentId} className="w-28">
                    <label
                      className={`block cursor-pointer rounded-lg border p-1.5 focus-within:outline focus-within:outline-2 focus-within:outline-ring ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card'
                      } ${blocked ? 'cursor-not-allowed opacity-40' : ''}`}
                      data-testid={`available-photo-${photo.attachmentId}`}
                    >
                      <span className="relative block h-20 w-full overflow-hidden rounded bg-muted">
                        <Thumb photo={photo} className="h-full w-full" />
                      </span>
                      <span className="mt-1 flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={blocked || busy}
                          onChange={() =>
                            isSelected ? deselect(photo.attachmentId) : select(photo.attachmentId)
                          }
                          className="h-3.5 w-3.5 shrink-0 accent-primary"
                          aria-label={`${isSelected ? 'Remove' : 'Add'} ${photo.originalFileName} ${
                            isSelected ? 'from' : 'to'
                          } the report`}
                          data-testid={`select-photo-${photo.attachmentId}`}
                        />
                        <span
                          className="truncate text-[10px] text-foreground"
                          title={photo.originalFileName}
                        >
                          {photo.originalFileName}
                        </span>
                      </span>
                      <span className="mt-1 block">
                        <OriginBadge origin={photo.origin} />
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {atMax && (
            <p
              className="text-xs text-muted-foreground"
              role="status"
              data-testid="photo-max-notice"
            >
              The report already holds the maximum of {MAX_PHOTOS} images. Remove one to add
              another.
            </p>
          )}
        </div>
      )}

      {/* ── Save / Cancel ────────────────────────────────────────────────── */}
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => saveMutation.mutate(draft)}
            disabled={busy || (!dirty && !convertible) || validationError !== null}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="save-report-photos"
          >
            {busy ? 'Saving…' : 'Save photos'}
          </button>
          <button
            type="button"
            onClick={() => setDraft(saved)}
            disabled={busy || !dirty}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="cancel-report-photos"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={busy || draft.length === 0}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="clear-report-photos"
          >
            Clear all
          </button>
          {dirty && !busy && (
            <span className="text-xs text-muted-foreground" data-testid="photos-unsaved">
              Unsaved changes
            </span>
          )}
          {!dirty && convertible && !busy && (
            <span className="text-xs text-muted-foreground" data-testid="photos-legacy-notice">
              This report still uses the automatic two-per-row layout. Saving keeps the arrangement
              shown above.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
